import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { mapToRushFeeTiers, type RushFeeTier } from "@src/appV2/redesign/utils/rushFee";
import { useGetWorkplaceChargeLeadTimeDifferentials } from "@src/appV2/redesign/WorkplaceChargeLeadTimeDifferentials/api/useGetWorkplaceChargeLeadTimeDifferentials";
import { useMemo } from "react";

interface UseTierRushFeeScheduleOptions {
  workplaceId: string;
  /**
   * Caller-specific gate layered on top of the feature flag (e.g. modal open,
   * shifts staged). Defaults to true — `useRushFeeCheck` fetches on hook mount
   * so the cache is warm before submit.
   */
  enabled?: boolean;
}

interface UseTierRushFeeScheduleResult {
  /** Sorted ascending by threshold. Empty when the tier-check flag is off. */
  tierSchedule: RushFeeTier[];
  /**
   * True when admins see the tier schedule in the workplace drawer but tier
   * resolution is unavailable here (flag off, fetch errored, or still loading).
   * Callers must short-circuit their rush-fee gate when this is true — applying
   * the legacy fee would contradict what the drawer advertises.
   */
  skipRushFeeChecks: boolean;
}

export function useTierRushFeeSchedule(
  options: UseTierRushFeeScheduleOptions
): UseTierRushFeeScheduleResult {
  const { workplaceId, enabled = true } = options;

  const checkTieredScheduleEnabled = useCbhFlag(
    CbhFeatureFlag.CHECK_TIERED_RUSH_FEE_SCHEDULE_IN_CONFIRMATION_DIALOG,
    { defaultValue: false }
  );
  const isRushFeeScheduleInDrawerEnabled = useCbhFlag(
    CbhFeatureFlag.SHOW_RUSH_FEE_SCHEDULE_IN_WORKPLACE_DRAWER,
    { defaultValue: false }
  );

  const query = useGetWorkplaceChargeLeadTimeDifferentials(
    { workplaceId },
    { enabled: enabled && checkTieredScheduleEnabled }
  );

  // The query key is shared across DailyView/MonthlyView callers, so when the
  // flag is off a sibling caller's cached payload could leak in here — gate the
  // mapping on the flag too.
  const tierSchedule = useMemo<RushFeeTier[]>(
    () => (checkTieredScheduleEnabled ? mapToRushFeeTiers(query.data?.data) : []),
    [checkTieredScheduleEnabled, query.data]
  );

  const skipRushFeeChecks =
    isRushFeeScheduleInDrawerEnabled &&
    (!checkTieredScheduleEnabled || query.isError || query.isLoading);

  return { tierSchedule, skipRushFeeChecks };
}
